package com.exura.pages.planDistribution

import androidx.compose.runtime.*
import com.exura.components.buttons.AsyncButton
import com.exura.components.buttons.ButtonPrimaryVariant
import com.exura.components.buttons.ButtonRoundVariant
import com.exura.components.icons.UntiledIcon
import com.exura.components.inputs.InlineInputGhostVariant
import com.exura.components.inputs.InlineTextInput
import com.exura.components.inputs.InlineTextInputWithAction
import com.exura.components.layouts.NavigableLayoutScope
import com.exura.components.loading.Loader
import com.exura.components.pageTypography.DisabledCardEntryVariant
import com.exura.components.pageTypography.ExuraBodyHeaderTextVariant
import com.exura.components.pageTypography.ExuraCommentaryTextVariant
import com.exura.components.pageTypography.ExuraErrorTextVariant
import com.exura.components.pageTypography.ExuraTextStyle
import com.exura.components.pageTypography.GeneralCardEntry
import com.exura.components.pageTypography.GhostCardEntryVariant
import com.exura.components.pageTypography.HighlightedCardEntryVariant
import com.exura.components.pageTypography.InteractiveCardEntryVariant
import com.exura.components.pageTypography.PageErrorContent
import com.exura.components.pageTypography.PageMainContent
import com.exura.components.pageTypography.PageNavigationHeaderSection
import com.exura.components.pageTypography.PageSecondaryContent
import com.exura.components.pageTypography.TitledSection
import com.exura.components.pageTypography.UnderwrittenText
import com.exura.features.plandefinition.domain.model.metadata.PlanDefinitionStatistics
import com.exura.features.plandefinition.planLocalization
import com.exura.features.plandefinition.presentation.PlanDefinitionDetails
import com.exura.features.planDistribution.distLocalization
import com.exura.navigation.Route
import com.exura.ui.Assets
import com.exura.ui.Dimens
import com.varabyte.kobweb.compose.css.AlignSelf
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.flexShrink
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.datetime.LocalDateTime
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Input

@Page(Route.WithParams.PLAN_DISTRIBUTION_DETAILS)
@Layout(".components.layouts.NavigableLayout")
@Composable
fun NavigableLayoutScope.PlanDistributionPage() {
    val context = rememberPageContext()
    val scope = rememberCoroutineScope()

    val planId = context.route.params[Route.Params.PLAN_ID]
    LaunchedEffect(planId) {
        if (planId.isNullOrBlank()) {
            context.router.navigateTo(Route.DASHBOARD)
        }
    }
    if (planId.isNullOrBlank()) return

    val controller = rememberPlanDistributionController(scope, context, planId)
    val state = controller.uiState

    Column(modifier = Modifier.fillMaxSize()) {
        PageNavigationHeaderSection(
            planLocalization.current.distribution.planDistribution,
            state.planVersion?.metadata?.fields?.title ?: "",
            isPageWorking = state.isLoading || state.isSendingPlan,
            onBack = { controller.handleEvent(PlanDistributionEvent.GoBack) },
        )

        when {
            state.isLoading && state.planVersion == null -> Loader()
            state.error != null -> PageErrorContent(errorMessage = state.error)
            state.planVersion != null -> PlanDistributionView(
                modifier = Modifier.weight(1f),
                state = state,
                onEvent = controller::handleEvent,
                showSnackbar = ::showSnackbar,
            )
        }
    }
}

@Composable
private fun PlanDistributionView(
    modifier: Modifier = Modifier,
    state: PlanDistributionUiState,
    onEvent: (PlanDistributionEvent) -> Unit,
    showSnackbar: (String) -> Unit,
) {
    PageMainContent(modifier) {
        Column(modifier = Modifier.fillMaxSize()) {
            PageSecondaryContent(modifier = Modifier.weight(1f)) {
                Column(
                    modifier = Modifier.fillMaxSize(),
                    verticalArrangement = Arrangement.spacedBy(Dimens.pageSectionDefaultGap)
                ) {
                    state.planVersion?.let { version ->
                        PlanSummarySection(
                            title = version.metadata?.fields?.title ?: "",
                            description = version.metadata?.fields?.description ?: "",
                            stats = version.metadata?.statistics ?: PlanDefinitionStatistics(0, 0, 0, 0, 0),
                        )
                    }

                    DistributionOptionsSection(
                        selectedOption = state.selectedDistributionOption,
                        onOptionSelected = { option ->
                            if (option == DistributionOption.PATIENT_CODE) {
                                onEvent(PlanDistributionEvent.SelectDistributionOption(option))
                            } else {
                                showSnackbar(distLocalization.current.options.comingSoon)
                            }
                        },
                    )

                    if (state.selectedDistributionOption == DistributionOption.PATIENT_CODE) {
                        PatientCodeDistributionSection(
                            state = state,
                            onEvent = onEvent,
                            showSnackbar = showSnackbar,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun PlanSummarySection(
    title: String,
    description: String,
    stats: PlanDefinitionStatistics,
) {
    TitledSection(title = planLocalization.current.metadata.planSummary) {
        GeneralCardEntry(variant = GhostCardEntryVariant) {
            PlanDefinitionDetails(
                modifier = Modifier.fillMaxWidth(),
                title = title,
                description = description,
                versionId = null,
                planDuration = stats.planDuration,
                numberInstruction = stats.numberInstruction,
                numberPersistent = stats.numberPersistent,
                numberQuestion = stats.numberQuestion,
                numberFloating = stats.numberFloating,
            )
        }
    }
}

@Composable
private fun DistributionOptionsSection(
    selectedOption: DistributionOption?,
    onOptionSelected: (DistributionOption) -> Unit,
) {
    TitledSection(title = distLocalization.current.options.distributionOptions) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(Dimens.cardEntriesBaseGap)
        ) {
            val defModifier = Modifier.weight(1f).fillMaxWidth().flexGrow(0)
            DistributionOptionCard(
                iconSrc = Assets.IconsUntiled.QR_CODE_DISTRIBUTION,
                title = distLocalization.current.options.generateQr,
                isSelected = selectedOption == DistributionOption.GENERATE_QR,
                isComingSoon = true,
                onClick = { onOptionSelected(DistributionOption.GENERATE_QR) },
                modifier = defModifier,
            )

            DistributionOptionCard(
                iconSrc = Assets.IconsUntiled.PRE_PRINTED_QR_CODE_DISTRIBUTION,
                title = distLocalization.current.options.prePrinted,
                isSelected = selectedOption == DistributionOption.PRE_PRINTED,
                isComingSoon = true,
                onClick = { onOptionSelected(DistributionOption.PRE_PRINTED) },
                modifier = defModifier,
            )

            DistributionOptionCard(
                iconSrc = Assets.IconsUntiled.CODE_PLAN_DISTRIBUTION,
                title = distLocalization.current.options.plansCode,
                isSelected = selectedOption == DistributionOption.PLANS_CODE,
                isComingSoon = true,
                onClick = { onOptionSelected(DistributionOption.PLANS_CODE) },
                modifier = defModifier,
            )

            DistributionOptionCard(
                iconSrc = Assets.IconsUntiled.LEGACY_PATIENTS_CODE_DISTRIBUTION,
                title = distLocalization.current.options.patientsCode,
                isSelected = selectedOption == DistributionOption.PATIENT_CODE,
                isComingSoon = false,
                onClick = { onOptionSelected(DistributionOption.PATIENT_CODE) },
                modifier = defModifier,
            )
        }
    }
}

@Composable
private fun DistributionOptionCard(
    iconSrc: String,
    title: String,
    isComingSoon: Boolean,
    isSelected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val variant =
        if (isComingSoon) DisabledCardEntryVariant
        else if (isSelected) HighlightedCardEntryVariant
        else InteractiveCardEntryVariant

    GeneralCardEntry(
        variant = variant,
        modifier = modifier
            .onClick { if (!isComingSoon) onClick() }
            .cursor(if (isComingSoon) Cursor.Default else Cursor.Pointer)
    ) {
        Column(
            modifier = Modifier.fillMaxSize(),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            UntiledIcon(
                iconSrc = iconSrc,
                modifier = Modifier.fontSize(Dimens.mainIconSizeDefault)
            )

            SpanText(
                title,
                modifier = ExuraTextStyle
                    .toModifier(ExuraBodyHeaderTextVariant)
                    .textAlign(TextAlign.Center)
            )

            SpanText(
                if (isComingSoon) distLocalization.current.options.comingSoon else " ",
                modifier = ExuraTextStyle
                    .toModifier(ExuraCommentaryTextVariant)
                    .textAlign(TextAlign.Center)
            )

        }
    }
}

@Composable
private fun PatientCodeDistributionSection(
    state: PlanDistributionUiState,
    onEvent: (PlanDistributionEvent) -> Unit,
    showSnackbar: (String) -> Unit,
) {
    TitledSection(title = distLocalization.current.patientCodeDistribution) {
        GeneralCardEntry(
            modifier = Modifier.fillMaxWidth(),
            variant = HighlightedCardEntryVariant,
        ) {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(Dimens.cardEntryTopBottomSpacer)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(Dimens.cardEntryLeftRightSpacer)
                ) {
                    DateTimeSelector(
                        selectedDateTime = state.selectedDateTime,
                        onDateTimeChange = { onEvent(PlanDistributionEvent.UpdateDateTime(it)) },
                        onErrorClear = { onEvent(PlanDistributionEvent.ClearError) },
                        modifier = Modifier.weight(1f).fillMaxWidth(),
                    )

                    PatientCodeInput(
                        patientCode = state.patientCode,
                        onPatientCodeChange = { onEvent(PlanDistributionEvent.UpdatePatientCode(it)) },
                        onErrorClear = { onEvent(PlanDistributionEvent.ClearError) },
                        isSendingPlan = state.isSendingPlan,
                        onSendClick = {
                            onEvent(
                                PlanDistributionEvent.DistributePlan(
                                    onSuccess = { showSnackbar("✅ $it") },
                                    onFailure = { showSnackbar("❌ $it") }
                                ))
                        },
                        modifier = Modifier.weight(1f),
                    )
                }

                if (state.distributionError.isNotEmpty()) {
                    SpanText(
                        state.distributionError,
                        modifier = ExuraTextStyle.toModifier(ExuraCommentaryTextVariant, ExuraErrorTextVariant)
                    )
                }
            }
        }
    }
}

@Composable
private fun DateTimeSelector(
    selectedDateTime: LocalDateTime?,
    onDateTimeChange: (LocalDateTime?) -> Unit,
    onErrorClear: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = Modifier.fillMaxWidth().then(modifier),
        verticalArrangement = Arrangement.spacedBy(Dimens.inputElementsDefaultGap)
    ) {
        InlineTextInput(
            inputType = InputType.DateTimeLocal,
            value = selectedDateTime?.toString()?.substring(0, 16) ?: "",
            onValueChange = { value ->
                try {
                    onDateTimeChange(LocalDateTime.parse(value))
                    onErrorClear()
                } catch (e: Exception) {
                }
            },
            modifier = Modifier.fillMaxWidth(),
            variant = InlineInputGhostVariant,
        )
//        Input(
//            type = InputType.DateTimeLocal,
//            attrs = {
//                style {
//                    width(100.percent)
//                    height(64.px)
//                    padding(Dimens.inputIndent)
//                    borderRadius(Dimens.inputBorderRadius)
//                    fontSize(Dimens.inputDefaultFontSize)
//                    property("box-sizing", "border-box")
//                }
//                value(selectedDateTime?.toString()?.substring(0, 16) ?: "")
//                onInput { event ->
//                    try {
//                        onDateTimeChange(LocalDateTime.parse(event.value))
//                        onErrorClear()
//                    } catch (e: Exception) {
//                    }
//                }
//            }
//        )
        SpanText(
            distLocalization.current.patientCode.selectDateAndTime,
            modifier = ExuraTextStyle.toModifier(ExuraCommentaryTextVariant)
        )
    }
}

@Composable
private fun PatientCodeInput(
    patientCode: String,
    onPatientCodeChange: (String) -> Unit,
    onErrorClear: () -> Unit,
    isSendingPlan: Boolean,
    onSendClick: suspend () -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = Modifier.fillMaxWidth().then(modifier),
        verticalArrangement = Arrangement.spacedBy(Dimens.inputElementsDefaultGap)
    ) {
        InlineTextInputWithAction(
            value = patientCode,
            onValueChange = {
                onPatientCodeChange(it)
                onErrorClear()
            },
            variant = InlineInputGhostVariant,
            placeholderText = "#123456789",
            transformValue = { "#" + it.substringAfterLast("#") },
            modifier = Modifier.fillMaxWidth(),
        ) {
            AsyncButton(
                text = "",
                onClick = onSendClick,
                variant = ButtonRoundVariant.then(ButtonPrimaryVariant),
                isDisabled = isSendingPlan,
                icon = { UntiledIcon(Assets.IconsUntiled.PROCEED) }
            )
        }
        SpanText(
            distLocalization.current.patientCode.patientCodeLabel,
            modifier = ExuraTextStyle.toModifier(ExuraCommentaryTextVariant)
        )

    }
}
